import { useReturnToPool } from "@components/campaigns/useReturnToPool";
import { CandidateReferenceBadge } from "@components/candidates/CandidateReferenceBadge";
import { QueryErrorState } from "@components/shared/QueryErrorState";
import { CREATE_OFFER, MOVE_CANDIDATE_STAGE, UPDATE_OFFER } from "@lib/graphql/mutations";
import { OFFER_STAGE_QUERY } from "@lib/graphql/queries";
import { useCampaignChannel } from "@lib/hooks/useCampaignChannel";
import { ChevronDown, ChevronRight, FileText, Plus } from "lucide-react";
import { useCallback, useState } from "react";
import { useMutation, useQuery } from "urql";
import { OfferForm } from "./OfferForm";
import { StageBadge } from "./StageBadge";
import { StageDropdown, TerminalStageMoveControl } from "./StageDropdown";

interface OfferStageProps {
  campaignId: string;
}

interface OfferData {
  id: string;
  status: string;
  baseSalary: number | null;
  currency: string | null;
  bonusDetails: string | null;
  equityDetails: string | null;
  startDate: string | null;
  notes: string | null;
  createdAt: string;
}

interface CampaignCandidateData {
  id: string;
  stage: string;
  candidate: {
    id: string;
    fullName: string;
    referenceNumber?: number;
    currentRoleTitle: string | null;
    currentCompany: string | null;
  };
  offers: OfferData[];
}

const STATUS_COLORS: Record<string, string> = {
  draft: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  extended: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  negotiating: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  accepted: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  declined: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  withdrawn: "bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400",
};

function formatSalary(amount: number | null, currency: string | null): string {
  if (amount == null) return "";
  const curr = currency ?? "GBP";
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: curr,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${curr} ${amount.toLocaleString()}`;
  }
}

export function OfferStage({ campaignId }: OfferStageProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [formId, setFormId] = useState<string | null>(null);
  const [editingOffer, setEditingOffer] = useState<OfferData | null>(null);

  const [{ data, fetching, error }, reexecute] = useQuery({
    query: OFFER_STAGE_QUERY,
    variables: { id: campaignId },
  });
  const [, moveStage] = useMutation(MOVE_CANDIDATE_STAGE);
  const [{ fetching: creating }, createOffer] = useMutation(CREATE_OFFER);
  const [{ fetching: updatingOffer }, updateOffer] = useMutation(UPDATE_OFFER);

  const allCandidates: CampaignCandidateData[] = data?.campaign?.campaignCandidates ?? [];
  const activeCandidates = allCandidates.filter((cc) => cc.stage === "offer");
  const movedOnCandidates = allCandidates.filter((cc) => cc.stage !== "offer");
  const refresh = useCallback(() => reexecute({ requestPolicy: "network-only" }), [reexecute]);
  useCampaignChannel(campaignId, refresh);

  const { openFor: openReturnToPool, dialog: returnToPoolDialog } = useReturnToPool(refresh);

  const handleMove = async (ccId: string, stage: string) => {
    await moveStage({ input: { campaignCandidateId: ccId, stage } });
    refresh();
  };

  const handleCreate = async (
    ccId: string,
    formData: {
      status: string;
      baseSalary: string;
      currency: string;
      bonusDetails: string;
      equityDetails: string;
      startDate: string;
      notes: string;
    },
  ) => {
    const input: Record<string, unknown> = { campaignCandidateId: ccId, status: formData.status };
    if (formData.baseSalary) input.baseSalary = Number(formData.baseSalary);
    if (formData.currency) input.currency = formData.currency;
    if (formData.bonusDetails) input.bonusDetails = formData.bonusDetails;
    if (formData.equityDetails) input.equityDetails = formData.equityDetails;
    if (formData.startDate) input.startDate = formData.startDate;
    if (formData.notes) input.notes = formData.notes;
    await createOffer({ input });
    setFormId(null);
    refresh();
  };

  const handleUpdate = async (
    offerId: string,
    formData: {
      status: string;
      baseSalary: string;
      currency: string;
      bonusDetails: string;
      equityDetails: string;
      startDate: string;
      notes: string;
    },
  ) => {
    const input: Record<string, unknown> = { id: offerId, status: formData.status };
    if (formData.baseSalary) input.baseSalary = Number(formData.baseSalary);
    if (formData.currency) input.currency = formData.currency;
    if (formData.bonusDetails) input.bonusDetails = formData.bonusDetails;
    if (formData.equityDetails) input.equityDetails = formData.equityDetails;
    if (formData.startDate) input.startDate = formData.startDate;
    if (formData.notes) input.notes = formData.notes;
    await updateOffer({ input });
    setEditingOffer(null);
    setExpandedId(null);
    refresh();
  };

  if (fetching && allCandidates.length === 0) {
    return <p className="text-sm text-text-muted">Loading offers...</p>;
  }

  const renderCandidate = (cc: CampaignCandidateData, movedPast: boolean) => {
    const isExpanded = expandedId === cc.id;
    const latestOffer = cc.offers[0];
    return (
      <div key={cc.id} className="border-b border-border">
        <div className="flex items-center gap-3 px-3 py-2">
          <button
            type="button"
            onClick={() => setExpandedId(isExpanded ? null : cc.id)}
            className="rounded p-0.5 text-text-faint hover:bg-card-hover"
          >
            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
          <div className="min-w-0 flex-1">
            {typeof cc.candidate.referenceNumber === "number" && (
              <>
                <CandidateReferenceBadge referenceNumber={cc.candidate.referenceNumber} />{" "}
              </>
            )}
            <span className="text-sm font-medium text-text-primary">{cc.candidate.fullName}</span>
            {(cc.candidate.currentRoleTitle || cc.candidate.currentCompany) && (
              <span className="ml-2 text-xs text-text-muted">
                {[cc.candidate.currentRoleTitle, cc.candidate.currentCompany]
                  .filter(Boolean)
                  .join(" at ")}
              </span>
            )}
          </div>
          {latestOffer && (
            <>
              <span
                className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_COLORS[latestOffer.status] ?? ""}`}
              >
                {latestOffer.status}
              </span>
              {latestOffer.baseSalary != null && (
                <span className="text-xs font-medium text-text-secondary">
                  {formatSalary(latestOffer.baseSalary, latestOffer.currency)}
                </span>
              )}
            </>
          )}
          {movedPast ? (
            <div className="flex items-center gap-1">
              <StageBadge stage={cc.stage} />
              <TerminalStageMoveControl
                stage={cc.stage}
                onMove={(stage) => handleMove(cc.id, stage)}
              />
            </div>
          ) : (
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => setFormId(formId === cc.id ? null : cc.id)}
                title="Create offer"
                className="flex items-center gap-1 rounded px-2 py-1 text-xs text-accent hover:bg-accent/5"
              >
                <Plus size={12} /> Offer
              </button>
              <StageDropdown
                currentStage="offer"
                onMove={(stage) => handleMove(cc.id, stage)}
                onReturnToPool={() => openReturnToPool(cc, "offer")}
              />
            </div>
          )}
        </div>

        {!movedPast && formId === cc.id && (
          <div className="mx-3 mb-3">
            <OfferForm
              onSave={(data) => handleCreate(cc.id, data)}
              onCancel={() => setFormId(null)}
              saving={creating}
            />
          </div>
        )}

        {isExpanded && (
          <div className="mx-10 mb-3 space-y-2">
            {cc.offers.length === 0 ? (
              <p className="text-xs text-text-faint">No offers recorded yet.</p>
            ) : (
              cc.offers.map((offer) => (
                <div key={offer.id} className="rounded border border-border bg-card-alt p-3">
                  {!movedPast && editingOffer?.id === offer.id ? (
                    <OfferForm
                      initial={offer}
                      onSave={(data) => handleUpdate(offer.id, data)}
                      onCancel={() => setEditingOffer(null)}
                      saving={updatingOffer}
                      isEdit
                    />
                  ) : (
                    <div>
                      <div className="mb-1 flex items-center gap-2">
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_COLORS[offer.status] ?? ""}`}
                        >
                          {offer.status}
                        </span>
                        {offer.baseSalary != null && (
                          <span className="text-xs font-medium text-text-primary">
                            {formatSalary(offer.baseSalary, offer.currency)}
                          </span>
                        )}
                        {offer.startDate && (
                          <span className="text-[10px] text-text-faint">
                            Start: {offer.startDate}
                          </span>
                        )}
                        <span className="text-[10px] text-text-faint">
                          {new Date(offer.createdAt).toLocaleDateString()}
                        </span>
                        {!movedPast && (
                          <button
                            type="button"
                            onClick={() => setEditingOffer(offer)}
                            className="ml-auto text-[10px] text-accent hover:underline"
                          >
                            Edit
                          </button>
                        )}
                      </div>
                      {offer.bonusDetails && (
                        <p className="text-xs text-text-muted">Bonus: {offer.bonusDetails}</p>
                      )}
                      {offer.equityDetails && (
                        <p className="text-xs text-text-muted">Equity: {offer.equityDetails}</p>
                      )}
                      {offer.notes && (
                        <p className="mt-1 text-xs text-text-secondary">{offer.notes}</p>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
        <div className="mb-3 flex items-center gap-2">
          <FileText size={16} className="text-text-faint" />
          <h3 className="text-sm font-medium text-text-secondary">Offers</h3>
          <span className="text-xs text-text-faint">
            {activeCandidates.length} active
            {movedOnCandidates.length > 0 && `, ${movedOnCandidates.length} moved on`}
          </span>
        </div>

        {error ? (
          <QueryErrorState
            error={error}
            what="offer candidates"
            onRetry={refresh}
            retrying={fetching}
          />
        ) : allCandidates.length === 0 ? (
          <p className="py-6 text-center text-sm text-text-faint">
            No candidates at offer stage. Move candidates from Client Interviews.
          </p>
        ) : (
          <div className="space-y-1">
            {activeCandidates.map((cc) => renderCandidate(cc, false))}
            {movedOnCandidates.map((cc) => renderCandidate(cc, true))}
          </div>
        )}
      </div>
      {returnToPoolDialog}
    </div>
  );
}
